import { useCallback, useMemo } from 'react';
import type { TFunction } from 'i18next';
import { customInstance } from '@/api/customInstance';
import { inventoryAirlineList } from '@/api/generated/inventory-airlines/inventory-airlines';
import type { AirlineListItem } from '@/api/generated/schemas';
import {
  AirlineCheckInType,
  AirlineStatus,
  AirlineTicketTypes,
  BaggageCategory,
} from '@/api/generated/schemas';
import type { CurrencyRef } from '@/modules/codebooks';
import { currencySelector } from '@/modules/codebooks';
import { Badge } from '@/shared/ui';
import {
  col,
  filter,
  type ListEnumOptionsSource,
  type ListExportColumn,
  type ListPageConfig,
  type ListQueryRequest,
} from '@/shared/ui/list-page';
import { NavListPage } from '@/shared/navigation/react/NavListPage';
import { useNavigator } from '@/shared/navigation/react/useNavigator';

import { airlineDetailAgenda } from '../detail/airlineDetailAgenda';
import { AIRLINE_LIST_KEY } from '../internal/airlinesQueryKeys';
import { airlineListAgenda } from './airlineListAgenda';

const STATUS_TONE = {
  [AirlineStatus.Active]: 'success',
  [AirlineStatus.Inactive]: 'neutral',
} as const;

/** Statické options enum filtru (hodnoty jsou součástí API kontraktu, ne číselník). */
function staticOptions(options: { id: string; label: string }[]): ListEnumOptionsSource {
  return { useOptions: () => ({ options, isLoading: false }) };
}

/** Okno online check-inu — dvě meze v hodinách před odletem; u letištního odbavení prázdné. */
function formatCheckInWindow(t: TFunction, item: AirlineListItem): string {
  const { checkInWindowFromHours: from, checkInWindowToHours: to } = item;
  if (from === null && to === null) {
    return t('airlineList.checkInWindow.none');
  }
  if (from !== null && to !== null) {
    return t('airlineList.checkInWindow.range', { from, to });
  }
  return from !== null
    ? t('airlineList.checkInWindow.from', { from })
    : t('airlineList.checkInWindow.to', { to });
}

/** Export leteckých společností do XLSX — Orval u binárky vrací string, voláme proto customInstance s blobem. */
function exportAirlines(
  request: ListQueryRequest,
  columns: ListExportColumn[],
  maxRows: number,
  signal?: AbortSignal
): Promise<Blob> {
  return customInstance<Blob>({
    url: '/api/inventory/airlines/list/export',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: { request, columns, maxRows },
    responseType: 'blob',
    signal,
  });
}

/** Šablona ListPage leteckých společností. */
function buildAirlineListConfig(
  openAirline: (id: number | undefined) => void
): ListPageConfig<AirlineListItem> {
  return {
    key: AIRLINE_LIST_KEY,
    i18n: { namespace: 'inventory' },
    api: {
      list: (request, signal) => inventoryAirlineList(request, signal),
      export: exportAirlines,
    },
    rowKey: (item) => item.airlineId,
    defaultSort: { column: 'tradeName', desc: false },
    fulltext: { placeholder: (t) => t('airlineList.search') },
    exportFileName: (t) => t('airlineList.exportFile'),
    onOpen: (item) => openAirline(item.airlineId),
    create: { label: (t) => t('airlineList.new'), onClick: () => openAirline(undefined) },
    // Taby prototypu „All / Active / Group booking" — „Vše" dodává engine jako systémový pohled.
    views: (t) => [
      {
        id: 'active',
        label: t('airlineList.views.active'),
        filters: [{ field: 'status', ids: [AirlineStatus.Active] }],
      },
      {
        id: 'groupBooking',
        label: t('airlineList.views.groupBooking'),
        filters: [
          {
            field: 'ticketTypes',
            ids: [AirlineTicketTypes.Group, AirlineTicketTypes.Both],
          },
        ],
      },
    ],
    columns: (t) => [
      col.mono<AirlineListItem>('airlineId', {
        label: t('airlineList.cols.id'),
        w: 80,
        sortKey: 'id',
        align: 'right',
      }),
      col.strong<AirlineListItem>('tradeName', {
        label: t('airlineList.cols.tradeName'),
        w: 220,
        frozen: true,
        sortKey: 'tradeName',
      }),
      col.mono<AirlineListItem>('iata', {
        label: t('airlineList.cols.iata'),
        w: 80,
        sortKey: 'iata',
      }),
      col.mono<AirlineListItem>('icao', {
        label: t('airlineList.cols.icao'),
        w: 80,
        sortKey: 'icao',
      }),
      col.text<AirlineListItem>('callsign', {
        label: t('airlineList.cols.callsign'),
        w: 140,
        visible: false,
        sortable: false,
      }),
      col.text<AirlineListItem>('organizationName', {
        label: t('airlineList.cols.organization'),
        w: 220,
        sortKey: 'organization',
      }),
      col.custom<AirlineListItem>({
        key: 'ticketTypes',
        label: t('airlineList.cols.ticketTypes'),
        w: 140,
        sortKey: 'ticketTypes',
        cell: (item) => <span>{t(`airlineTicketTypes.${item.ticketTypes}`)}</span>,
      }),
      col.custom<AirlineListItem>({
        key: 'allowsNameChanges',
        label: t('airlineList.cols.nameChanges'),
        w: 120,
        sortable: false,
        cell: (item) => (
          <span>{t(item.allowsNameChanges ? 'airlineList.yes' : 'airlineList.no')}</span>
        ),
      }),
      col.custom<AirlineListItem>({
        key: 'checkInType',
        label: t('airlineList.cols.checkInType'),
        w: 140,
        sortable: false,
        cell: (item) => <span>{t(`airlineCheckInType.${item.checkInType}`)}</span>,
      }),
      col.custom<AirlineListItem>({
        // Odvozený text ze dvou zdrojových hodnot — na serveru sloupec neexistuje, proto neřaditelný.
        // Klíč míří na `checkInWindowFromHours`, aby export (čte hodnoty z řádkového DTO reflexí podle
        // klíče) nesl aspoň začátek okna místo prázdné buňky.
        key: 'checkInWindowFromHours',
        label: t('airlineList.cols.checkInWindow'),
        w: 160,
        sortable: false,
        cell: (item) => <span>{formatCheckInWindow(t, item)}</span>,
      }),
      col.mono<AirlineListItem>('currencyCode', {
        label: t('airlineList.cols.currency'),
        w: 90,
        visible: false,
        sortable: false,
      }),
      col.num<AirlineListItem>('baggageRuleCount', {
        label: t('airlineList.cols.baggageRules'),
        w: 120,
        visible: false,
        sortable: false,
      }),
      col.custom<AirlineListItem>({
        key: 'status',
        label: t('airlineList.cols.status'),
        w: 104,
        sortKey: 'status',
        cell: (item) => (
          <Badge tone={STATUS_TONE[item.status]}>{t(`airlineStatus.${item.status}`)}</Badge>
        ),
      }),
    ],
    filterGroups: (t) => [
      { id: 'basic', label: t('airlineList.groups.basic') },
      { id: 'advanced', label: t('airlineList.groups.advanced') },
    ],
    filters: (t) => [
      filter.text('tradeName', {
        label: t('airlineList.filters.tradeName'),
        group: 'basic',
        freq: true,
      }),
      filter.text('iata', { label: t('airlineList.filters.iata'), group: 'basic', freq: true }),
      filter.text('icao', { label: t('airlineList.filters.icao'), group: 'basic', freq: true }),
      filter.enum('status', {
        label: t('airlineList.filters.status'),
        group: 'basic',
        freq: true,
        options: staticOptions(
          Object.values(AirlineStatus).map((id) => ({ id, label: t(`airlineStatus.${id}`) }))
        ),
      }),
      filter.enum('ticketTypes', {
        label: t('airlineList.filters.ticketTypes'),
        group: 'basic',
        freq: true,
        options: staticOptions(
          Object.values(AirlineTicketTypes).map((id) => ({
            id,
            label: t(`airlineTicketTypes.${id}`),
          }))
        ),
      }),
      filter.enum('allowsNameChanges', {
        label: t('airlineList.filters.nameChanges'),
        group: 'advanced',
        options: staticOptions([
          { id: 'true', label: t('airlineList.yes') },
          { id: 'false', label: t('airlineList.no') },
        ]),
      }),
      filter.enum('checkInType', {
        label: t('airlineList.filters.checkInType'),
        group: 'advanced',
        options: staticOptions(
          Object.values(AirlineCheckInType).map((id) => ({
            id,
            label: t(`airlineCheckInType.${id}`),
          }))
        ),
      }),
      filter.selector<CurrencyRef>('currency', {
        label: t('airlineList.filters.currency'),
        group: 'advanced',
        selector: currencySelector,
        getId: (item) => item.id,
        getLabel: (item) => item.code,
      }),
      filter.enum('baggageCategory', {
        label: t('airlineList.filters.baggageCategory'),
        group: 'advanced',
        options: staticOptions(
          Object.values(BaggageCategory).map((id) => ({ id, label: t(`baggageCategory.${id}`) }))
        ),
      }),
    ],
  };
}

/**
 * Agenda seznamu leteckých společností. Otevření řádku i akce „Nová letecká společnost" míří na
 * `airlineDetailAgenda` přes `openDetail` (výběr v listu určuje, co je otevřené v detail panelu).
 */
export function AirlineListPage({ layerId }: { layerId: string }) {
  const navigator = useNavigator();

  const openAirline = useCallback(
    (id: number | undefined) => {
      void navigator.openDetail(airlineDetailAgenda, { airlineId: id });
    },
    [navigator]
  );

  const config = useMemo(() => buildAirlineListConfig(openAirline), [openAirline]);

  return <NavListPage agenda={airlineListAgenda} layerId={layerId} config={config} />;
}
